import { EventEmitter } from "@stencil/core";
import { ScaleLinear } from "d3-scale";
import { BaseType, EnterElement, Selection } from "d3-selection";
import {
    curveBasis,
    curveBundle,
    curveCardinal,
    curveCatmullRom,
    CurveFactoryLineOnly,
    curveLinear,
    curveMonotoneX,
    curveNatural,
    curveStep,
    curveStepAfter,
    curveStepBefore,
    line,
} from "d3-shape";
import { Transition } from "d3-transition";
import {
    LineChartCurveFunctionName,
    LineChartData,
    LineChartDataItem,
} from "./line-chart";

type LineChartDotItem = { value: LineChartDataItem; dotSize: number };

export class LineChartSvgDataGroup {
    selection: any;
    curveFunctions: {
        [fnName in LineChartCurveFunctionName]: CurveFactoryLineOnly;
    };
    constructor(
        selection: Selection<
            SVGGElement | Element | EnterElement | Document | Window,
            LineChartData,
            BaseType,
            unknown
        >
    ) {
        this.selection = selection;

        this.curveFunctions = {
            curveLinear: curveLinear,
            curveStep: curveStep,
            curveStepBefore: curveStepBefore,
            curveStepAfter: curveStepAfter,
            curveBasis: curveBasis,
            curveBundle: curveBundle,
            curveCardinal: curveCardinal,
            curveMonotoneX: curveMonotoneX,
            curveNatural: curveNatural,
            curveCatmullRom: curveCatmullRom,
        };
    }

    public updateLines(
        xPositionForKeyFn: (key) => number,
        yScale,
        curveFnName: LineChartCurveFunctionName,
        transitionDurationMs: number,
        eventEmitter: EventEmitter<LineChartData>
    ) {
        const lines = line<{ key: Date | string; value: number }>()
            .x((d) => xPositionForKeyFn(d.key))
            .y((d) => yScale(+d.value))
            .curve(this.curveFunctions[curveFnName] || curveLinear);

        this.selection
            .selectAll(".uic-line-chart__line")
            .data((d) => [d])
            .join(
                (enter) =>
                    enter
                        .append("path")
                        .style("fill", "none")
                        .attr("clip-path", "url(#clip)")
                        .attr("class", "uic-line-chart__line")
                        .attr("d", (d) => lines(d.values))
                        .on("click touchstart", (e, d) => {
                            e.preventDefault();
                            e.stopPropagation();
                            eventEmitter.emit(d);
                        }),
                (update) =>
                    update
                        .transition()
                        .duration(transitionDurationMs)
                        .attr("d", (d) => lines(d.values))
                        .selection()
            );

        return this;
    }

    public updateDots(
        xPositionForKeyFn: (key) => number,
        yScale,
        transitionDurationMs: number,
        hideDots: boolean,
        eventEmitter: EventEmitter<LineChartData>
    ) {
        this.selection
            .selectAll(".uic-line-chart__dots")
            .data(function (d: LineChartData) {
                if (hideDots) return [];

                const strokeWidth = getComputedStyle(this).strokeWidth;
                const dotSize = strokeWidth.endsWith("px")
                    ? parseInt(strokeWidth.slice(0, strokeWidth.length - 2)) * 3
                    : 9; // 3 times the default stroke width, just as fallback

                return d.values.map((v) => ({ ...d, value: v, dotSize }));
            })
            .join(
                (enter) =>
                    enter
                        .append("rect")
                        .attr("class", "uic-line-chart__dots")
                        .call((d) =>
                            LineChartSvgDataGroup.positionDots(
                                d,
                                xPositionForKeyFn,
                                yScale
                            )
                        )
                        .on("click touchstart", (e, d) => {
                            e.preventDefault();
                            e.stopPropagation();
                            eventEmitter.emit(d);
                        }),
                (update) =>
                    update
                        .transition()
                        .duration(transitionDurationMs)
                        .call((d) =>
                            LineChartSvgDataGroup.positionDots(
                                d,
                                xPositionForKeyFn,
                                yScale
                            )
                        )
                        .selection()
            );
        return this;
    }

    private static positionDots(
        selection:
            | Selection<SVGRectElement, LineChartDotItem, BaseType, unknown>
            | Transition<SVGRectElement, LineChartDotItem, BaseType, unknown>,
        xPositionForKeyFn: (key) => number,
        yScale: ScaleLinear<number, number>
    ) {
        selection
            .attr("width", ({ dotSize }) => dotSize)
            .attr("height", ({ dotSize }) => dotSize)
            .attr(
                "x",
                ({ value: d, dotSize }) =>
                    xPositionForKeyFn(d.key) - dotSize / 2
            )
            .attr(
                "y",
                ({ value: d, dotSize }) => yScale(+d.value) - dotSize / 2
            );
    }
}
